/**
 * Admin Page - Update site capacity and cost data, create new sites
 * Requirements: 8.1, 8.2, 8.3, 8.4
 */

import { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../store/hooks';
import {
  fetchSites,
  fetchSiteDetail,
  updateSite,
  createSite,
  SiteSummary,
  SiteDetail,
  CapacityRow,
  CostParameters,
  SiteCapabilities,
} from '../services/api';

interface ValidationError {
  field: string;
  message: string;
}

type AdminMode = 'edit' | 'create';

function Admin() {
  const { user } = useAppSelector((state) => state.auth);
  const [mode, setMode] = useState<AdminMode>('edit');
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [siteDetail, setSiteDetail] = useState<SiteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // Edit mode form state
  const [capacityData, setCapacityData] = useState<CapacityRow[]>([]);
  const [costParams, setCostParams] = useState<CostParameters | null>(null);

  // Create mode form state
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteLocation, setNewSiteLocation] = useState('');
  const [newCapabilities, setNewCapabilities] = useState<SiteCapabilities>({
    operatingSchedule: '24/5',
    availableFormats: [],
    totalLines: 1,
    activeLines: 1,
    minimumOrderQuantity: 1000,
    description: '',
  });
  const [newCostParams, setNewCostParams] = useState<CostParameters>({
    totalBudget: 10000000,
    fixedCostPercentage: 0.6,
    fixedCost: 6000000,
    incrementalCaseImpact: 0.03,
    extendedValuePerMillion: 1000000,
  });

  const [newFormats, setNewFormats] = useState<string>('');

  // Load sites on mount
  useEffect(() => {
    async function loadSites() {
      try {
        const data = await fetchSites();
        setSites(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sites');
      }
    }
    loadSites();
  }, []);

  // Load site detail when selection changes
  useEffect(() => {
    if (!selectedSiteId || mode === 'create') {
      setSiteDetail(null);
      setCapacityData([]);
      setCostParams(null);
      return;
    }

    async function loadSiteDetail() {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setValidationErrors([]);
      try {
        const data = await fetchSiteDetail(selectedSiteId);
        setSiteDetail(data);
        setCapacityData(data.capacity);
        setCostParams(data.costParameters);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load site details');
      } finally {
        setLoading(false);
      }
    }
    loadSiteDetail();
  }, [selectedSiteId, mode]);

  // Validation function for edit mode
  const validateEditForm = useCallback((): ValidationError[] => {
    const errors: ValidationError[] = [];
    capacityData.forEach((row, index) => {
      if (row.loadCapacity !== null && row.plannedCases > row.loadCapacity) {
        errors.push({
          field: `capacity-${index}-plannedCases`,
          message: `${row.format}: Planned cases exceeds load capacity`,
        });
      }
    });
    if (costParams && (costParams.fixedCostPercentage < 0 || costParams.fixedCostPercentage > 1)) {
      errors.push({ field: 'fixedCostPercentage', message: 'Fixed cost percentage must be between 0 and 1' });
    }
    return errors;
  }, [capacityData, costParams]);

  // Validation function for create mode
  const validateCreateForm = useCallback((): ValidationError[] => {
    const errors: ValidationError[] = [];
    if (!newSiteName.trim()) errors.push({ field: 'name', message: 'Site name is required' });
    if (!newSiteLocation.trim()) errors.push({ field: 'location', message: 'Location is required' });
    if (newCostParams.totalBudget <= 0) errors.push({ field: 'totalBudget', message: 'Budget must be greater than 0' });
    if (newCostParams.fixedCostPercentage < 0 || newCostParams.fixedCostPercentage > 1) {
      errors.push({ field: 'fixedCostPercentage', message: 'Fixed cost percentage must be between 0 and 1' });
    }
    return errors;
  }, [newSiteName, newSiteLocation, newCostParams]);


  // Handle capacity row change
  const handleCapacityChange = (index: number, field: keyof CapacityRow, value: number) => {
    setCapacityData(prev => {
      const updated = [...prev];
      const row = updated[index];
      if (!row) return prev;
      updated[index] = { ...row, [field]: value };
      return updated;
    });
    setValidationErrors([]);
  };

  // Handle cost parameter change
  const handleCostChange = (field: keyof CostParameters, value: number) => {
    setCostParams(prev => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };
      if (field === 'fixedCostPercentage' || field === 'totalBudget') {
        updated.fixedCost = updated.totalBudget * updated.fixedCostPercentage;
      }
      return updated;
    });
    setValidationErrors([]);
  };

  // Handle new cost parameter change
  const handleNewCostChange = (field: keyof CostParameters, value: number) => {
    setNewCostParams(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'fixedCostPercentage' || field === 'totalBudget') {
        updated.fixedCost = updated.totalBudget * updated.fixedCostPercentage;
      }
      return updated;
    });
    setValidationErrors([]);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateEditForm();
    if (errors.length > 0) { setValidationErrors(errors); return; }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await updateSite(selectedSiteId, { capacity: capacityData, costParameters: costParams || undefined });
      setSuccess('Site data updated successfully!');
      const data = await fetchSiteDetail(selectedSiteId);
      setSiteDetail(data);
      setCapacityData(data.capacity);
      setCostParams(data.costParameters);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update site');
    } finally {
      setSaving(false);
    }
  };


  // Handle create form submission
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateCreateForm();
    if (errors.length > 0) { setValidationErrors(errors); return; }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const formats = newFormats.split(',').map(f => f.trim()).filter(f => f.length > 0);
      const result = await createSite({
        name: newSiteName.trim(),
        location: newSiteLocation.trim(),
        capabilities: { ...newCapabilities, availableFormats: formats },
        costParameters: newCostParams,
      });
      setSuccess(`Site "${result.site.name}" created successfully!`);
      // Refresh sites list
      const updatedSites = await fetchSites();
      setSites(updatedSites);
      // Reset form
      setNewSiteName('');
      setNewSiteLocation('');
      setNewFormats('');
      setMode('edit');
      setSelectedSiteId(result.site.siteId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setSaving(false);
    }
  };

  // Check if user has finance role
  if (user?.role !== 'finance') {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need Finance role to access the Admin panel.</p>
          <p>Current role: {user?.role || 'Not logged in'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Administration</h1>
      <p style={styles.subtitle}>Manage sites and capacity data (Finance role required)</p>

      {/* Mode Toggle */}
      <div style={styles.modeToggle}>
        <button
          onClick={() => setMode('edit')}
          style={{ ...styles.modeButton, ...(mode === 'edit' ? styles.modeButtonActive : {}) }}
        >
          Edit Existing Site
        </button>
        <button
          onClick={() => setMode('create')}
          style={{ ...styles.modeButton, ...(mode === 'create' ? styles.modeButtonActive : {}) }}
        >
          + Create New Site
        </button>
      </div>

      {/* Messages */}
      {error && <div style={styles.errorMessage}>{error}</div>}
      {success && <div style={styles.successMessage}>{success}</div>}
      {validationErrors.length > 0 && (
        <div style={styles.validationErrors}>
          <strong>Please fix the following errors:</strong>
          <ul>{validationErrors.map((err, i) => <li key={i}>{err.message}</li>)}</ul>
        </div>
      )}


      {mode === 'edit' ? (
        <>
          {/* Site Selector */}
          <div style={styles.selectorSection}>
            <label style={styles.label}>Select Site:</label>
            <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)} style={styles.select}>
              <option value="">-- Select a site --</option>
              {sites.map((site) => (
                <option key={site.siteId} value={site.siteId}>{site.name} ({site.location})</option>
              ))}
            </select>
          </div>

          {loading && <div style={styles.loading}>Loading site data...</div>}

          {siteDetail && !loading && (
            <form onSubmit={handleEditSubmit}>
              <section style={styles.section}>
                <h2 style={styles.sectionTitle}>Capacity Data</h2>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Format</th>
                        <th style={styles.th}>Planned Cases</th>
                        <th style={styles.th}>Load Capacity</th>
                        <th style={styles.th}>Utilization</th>
                      </tr>
                    </thead>
                    <tbody>
                      {capacityData.map((row, index) => {
                        const utilization = row.loadCapacity ? row.plannedCases / row.loadCapacity : 1;
                        return (
                          <tr key={row.format}>
                            <td style={styles.td}>{row.format}</td>
                            <td style={styles.td}>
                              <input type="number" value={row.plannedCases}
                                onChange={(e) => handleCapacityChange(index, 'plannedCases', parseInt(e.target.value) || 0)}
                                style={styles.input} />
                            </td>
                            <td style={styles.td}>{row.loadCapacity?.toLocaleString() || 'N/A'}</td>
                            <td style={styles.td}>
                              <span style={{ color: utilization > 1 ? '#dc2626' : utilization > 0.95 ? '#f59e0b' : '#10b981' }}>
                                {(utilization * 100).toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              {costParams && (
                <section style={styles.section}>
                  <h2 style={styles.sectionTitle}>Cost Parameters</h2>
                  <div style={styles.formGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Total Budget ($)</label>
                      <input type="number" value={costParams.totalBudget}
                        onChange={(e) => handleCostChange('totalBudget', parseFloat(e.target.value) || 0)} style={styles.input} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Fixed Cost %</label>
                      <input type="number" step="0.01" min="0" max="1" value={costParams.fixedCostPercentage}
                        onChange={(e) => handleCostChange('fixedCostPercentage', parseFloat(e.target.value) || 0)} style={styles.input} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Fixed Cost (calc)</label>
                      <input type="text" value={`$${(costParams.fixedCost / 1000000).toFixed(2)}MM`} disabled style={{ ...styles.input, ...styles.inputDisabled }} />
                    </div>
                  </div>
                </section>
              )}

              <div style={styles.buttonContainer}>
                <button type="submit" disabled={saving} style={{ ...styles.submitButton, ...(saving ? styles.submitButtonDisabled : {}) }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </>
      ) : (

        /* Create New Site Form */
        <form onSubmit={handleCreateSubmit}>
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Site Information</h2>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Site Name *</label>
                <input type="text" value={newSiteName} onChange={(e) => setNewSiteName(e.target.value)}
                  placeholder="e.g., Fresno Bottling" style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Location *</label>
                <input type="text" value={newSiteLocation} onChange={(e) => setNewSiteLocation(e.target.value)}
                  placeholder="e.g., Fresno, CA" style={styles.input} />
              </div>
            </div>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Capabilities</h2>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Operating Schedule</label>
                <select value={newCapabilities.operatingSchedule}
                  onChange={(e) => setNewCapabilities(prev => ({ ...prev, operatingSchedule: e.target.value }))} style={styles.select}>
                  <option value="24/7">24/7</option>
                  <option value="24/5">24/5</option>
                  <option value="16/5">16/5 (Two shifts)</option>
                  <option value="8/5">8/5 (Single shift)</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Total Lines</label>
                <input type="number" min="1" value={newCapabilities.totalLines}
                  onChange={(e) => setNewCapabilities(prev => ({ ...prev, totalLines: parseInt(e.target.value) || 1 }))} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Active Lines</label>
                <input type="number" min="1" value={newCapabilities.activeLines}
                  onChange={(e) => setNewCapabilities(prev => ({ ...prev, activeLines: parseInt(e.target.value) || 1 }))} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Min Order Qty</label>
                <input type="number" min="1" value={newCapabilities.minimumOrderQuantity}
                  onChange={(e) => setNewCapabilities(prev => ({ ...prev, minimumOrderQuantity: parseInt(e.target.value) || 1000 }))} style={styles.input} />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Available Formats (comma-separated)</label>
                <input type="text" value={newFormats} onChange={(e) => setNewFormats(e.target.value)}
                  placeholder="e.g., 750ml Glass, 1.5L PET, 3L Box" style={styles.input} />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Description</label>
                <textarea value={newCapabilities.description}
                  onChange={(e) => setNewCapabilities(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the site..." style={{ ...styles.input, minHeight: '60px' }} />
              </div>
            </div>
          </section>


          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Cost Parameters</h2>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Total Budget ($) *</label>
                <input type="number" value={newCostParams.totalBudget}
                  onChange={(e) => handleNewCostChange('totalBudget', parseFloat(e.target.value) || 0)} style={styles.input} />
                <span style={styles.hint}>${(newCostParams.totalBudget / 1000000).toFixed(2)}MM</span>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fixed Cost % *</label>
                <input type="number" step="0.01" min="0" max="1" value={newCostParams.fixedCostPercentage}
                  onChange={(e) => handleNewCostChange('fixedCostPercentage', parseFloat(e.target.value) || 0)} style={styles.input} />
                <span style={styles.hint}>{(newCostParams.fixedCostPercentage * 100).toFixed(0)}%</span>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fixed Cost (calculated)</label>
                <input type="text" value={`$${(newCostParams.fixedCost / 1000000).toFixed(2)}MM`} disabled style={{ ...styles.input, ...styles.inputDisabled }} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Incremental Case Impact ($)</label>
                <input type="number" step="0.01" value={newCostParams.incrementalCaseImpact}
                  onChange={(e) => handleNewCostChange('incrementalCaseImpact', parseFloat(e.target.value) || 0)} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Extended Value per 1MM ($)</label>
                <input type="number" value={newCostParams.extendedValuePerMillion}
                  onChange={(e) => handleNewCostChange('extendedValuePerMillion', parseFloat(e.target.value) || 0)} style={styles.input} />
              </div>
            </div>
          </section>

          <div style={styles.buttonContainer}>
            <button type="button" onClick={() => setMode('edit')} style={styles.cancelButton}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...styles.submitButton, ...(saving ? styles.submitButtonDisabled : {}) }}>
              {saving ? 'Creating...' : 'Create Site'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: '1000px', margin: '0 auto', padding: '1rem' },
  title: { margin: '0 0 0.5rem 0', fontSize: '1.75rem', color: '#333' },
  subtitle: { margin: '0 0 1.5rem 0', color: '#666' },
  accessDenied: { textAlign: 'center', padding: '3rem', backgroundColor: '#fef2f2', borderRadius: '8px', color: '#991b1b' },
  modeToggle: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' },
  modeButton: { padding: '0.75rem 1.5rem', fontSize: '0.95rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' },
  modeButtonActive: { backgroundColor: '#2563eb', color: '#fff', borderColor: '#2563eb' },
  selectorSection: { marginBottom: '1.5rem' },
  label: { display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' },
  select: { width: '100%', maxWidth: '400px', padding: '0.75rem', fontSize: '1rem', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff' },
  loading: { textAlign: 'center', padding: '2rem', color: '#666' },
  errorMessage: { padding: '1rem', marginBottom: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b' },
  successMessage: { padding: '1rem', marginBottom: '1rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', color: '#166534' },
  validationErrors: { padding: '1rem', marginBottom: '1rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', color: '#92400e' },
  section: { backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#333', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' },
  tableContainer: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  td: { padding: '0.75rem', borderBottom: '1px solid #e5e7eb' },
  input: { width: '100%', padding: '0.5rem', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '4px' },
  inputDisabled: { backgroundColor: '#f3f4f6', color: '#6b7280' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  hint: { fontSize: '0.8rem', color: '#6b7280' },
  buttonContainer: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' },
  submitButton: { padding: '0.75rem 2rem', fontSize: '1rem', fontWeight: 500, color: '#fff', backgroundColor: '#2563eb', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  submitButtonDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  cancelButton: { padding: '0.75rem 1.5rem', fontSize: '1rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' },
};

export default Admin;